import React, { useState, useEffect } from 'react';
import { ChevronDown, Shield, Users, Globe, ServerCog, MessageSquare, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n/LanguageContext';

export default function DerrDigitalDatenschutz({ darkMode = false }) {
  const { t } = useTranslation();
  const [openSection, setOpenSection] = useState('allgemein');

  const bg          = darkMode ? '#0a0a0f' : '#f8faff';
  const textPrimary = darkMode ? '#ffffff' : '#0f0f1a';
  const textSecondary = darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(15,15,26,0.55)';
  const cardBg      = darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.9)';
  const cardBorder  = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  const accent      = '#6366f1';
  const accent2     = '#8b5cf6';

  useEffect(() => {
    document.documentElement.style.background = bg;
    document.body.style.background = bg;
    return () => { document.documentElement.style.background = ''; document.body.style.background = ''; };
  }, [bg]);

  const sections = [
    { key: 'allgemein',      Icon: Shield,       titleKey: 'datenschutz.sec1Title', content: (
      <><p>{t('datenschutz.sec1P1')}</p><p>{t('datenschutz.sec1P2')}</p><p>{t('datenschutz.sec1P3')}</p></>
    )},
    { key: 'datenerhebung',  Icon: Users,        titleKey: 'datenschutz.sec2Title', content: (
      <><h3>{t('datenschutz.sec2H1')}</h3><p>{t('datenschutz.sec2P1')}</p><h3>{t('datenschutz.sec2H2')}</h3><p>{t('datenschutz.sec2P2')}</p><h3>{t('datenschutz.sec2H3')}</h3><p>{t('datenschutz.sec2P3')}</p></>
    )},
    { key: 'cookies',        Icon: Globe,        titleKey: 'datenschutz.sec3Title', content: (
      <><h3>{t('datenschutz.sec3H1')}</h3><p>{t('datenschutz.sec3P1')}</p><p>{t('datenschutz.sec3P2')}</p><h3>{t('datenschutz.sec3H2')}</h3><ul><li>{t('datenschutz.sec3Cookie1')}</li><li>{t('datenschutz.sec3Cookie2')}</li><li>{t('datenschutz.sec3Cookie3')}</li></ul><h3>{t('datenschutz.sec3H3')}</h3><p>{t('datenschutz.sec3P3')}</p></>
    )},
    { key: 'sicherheit',     Icon: ServerCog,    titleKey: 'datenschutz.sec4Title', content: (
      <><p>{t('datenschutz.sec4P1')}</p><p>{t('datenschutz.sec4P2')}</p><p>{t('datenschutz.sec4P3')}</p></>
    )},
    { key: 'rechte',         Icon: MessageSquare,titleKey: 'datenschutz.sec5Title', content: (
      <><p>{t('datenschutz.sec5P1')}</p><p>{t('datenschutz.sec5P2')}</p><ul>{['sec5Right1','sec5Right2','sec5Right3','sec5Right4','sec5Right5','sec5Right6'].map(k=><li key={k}>{t(`datenschutz.${k}`)}</li>)}</ul><p>{t('datenschutz.sec5P3')}</p></>
    )},
    { key: 'kontakt',        Icon: Mail,         titleKey: 'datenschutz.sec6Title', content: (
      <><p>{t('datenschutz.sec6P1')}</p><p><strong>Dustin Derr GbR</strong><br />Alte Molkerei 4<br />49692 Cappeln (Oldenburg)<br />Deutschland</p><p>E-Mail: datenschutz@derrdigital.de<br />Telefon: +49 (0) 174 1628004</p></>
    )},
  ];

  return (
    <div style={{ background: bg, color: textPrimary, minHeight: '100vh' }}>
      <style>{`
        .ds-prose p{font-size:13px;color:${textSecondary};line-height:1.7;margin-bottom:10px}
        .ds-prose p:last-child{margin-bottom:0}
        .ds-prose h3{font-size:13px;font-weight:700;color:${textPrimary};margin:14px 0 6px}
        .ds-prose ul{padding-left:18px;margin-bottom:10px}
        .ds-prose li{font-size:13px;color:${textSecondary};line-height:1.65;margin-bottom:4px}
        .ds-prose strong{color:${textPrimary}}
      `}</style>

      {/* Hero */}
      <section style={{ position: 'relative', paddingTop: 'clamp(90px, 13vh, 120px)', paddingBottom: 48, overflow: 'hidden', textAlign: 'center' }}>
        <div style={{ position: 'absolute', inset: 0, background: darkMode ? 'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(99,102,241,0.12) 0%, transparent 65%)' : 'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(99,102,241,0.07) 0%, transparent 65%)' }} />
        <div style={{ maxWidth: 680, margin: '0 auto', padding: '0 24px', position: 'relative' }}>
          <div style={{ width: 48, height: 48, borderRadius: 13, background: accent + '18', border: `1px solid ${accent}35`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', color: accent }}>
            <Shield size={22} strokeWidth={1.5} />
          </div>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', color: accent2, textTransform: 'uppercase', marginBottom: 10 }}>Rechtliches</div>
          <h1 style={{ fontSize: 'clamp(24px, 4vw, 48px)', fontWeight: 900, lineHeight: 1.05, letterSpacing: '-0.02em', marginBottom: 10 }}>{t('datenschutz.title')}</h1>
          <p style={{ fontSize: 14, color: textSecondary, lineHeight: 1.65 }}>{t('datenschutz.subtitle')}</p>
        </div>
      </section>

      {/* Accordion sections */}
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 24px 80px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {sections.map(({ key, Icon, titleKey, content }) => {
          const isOpen = openSection === key;
          return (
            <div key={key} style={{ background: cardBg, border: `1px solid ${isOpen ? accent + '40' : cardBorder}`, borderRadius: 12, overflow: 'hidden', transition: 'border-color 0.2s' }}>
              <button onClick={() => setOpenSection(isOpen ? null : key)} style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 18px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', gap: 12,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: isOpen ? accent + '18' : (darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'), display: 'flex', alignItems: 'center', justifyContent: 'center', color: isOpen ? accent : textSecondary, flexShrink: 0, transition: 'all 0.2s' }}>
                    <Icon size={14} strokeWidth={1.8} />
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 700, color: isOpen ? textPrimary : textSecondary, transition: 'color 0.2s' }}>{t(titleKey)}</span>
                </div>
                <ChevronDown size={15} color={textSecondary} style={{ flexShrink: 0, transition: 'transform 0.3s', transform: isOpen ? 'rotate(180deg)' : 'none' }} />
              </button>
              {isOpen && (
                <div style={{ padding: '0 18px 16px', borderTop: `1px solid ${cardBorder}`, paddingTop: 14 }}>
                  <div className="ds-prose">{content}</div>
                </div>
              )}
            </div>
          );
        })}

        <p style={{ textAlign: 'center', fontSize: 12, color: textSecondary, marginTop: 8 }}>{t('datenschutz.lastUpdated')}</p>
      </div>

      {/* CTA */}
      <section style={{ padding: 'clamp(36px, 5vh, 56px) 0', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.25)' }} />
        <div style={{ maxWidth: 560, margin: '0 auto', padding: '0 24px', textAlign: 'center', position: 'relative' }}>
          <h2 style={{ fontSize: 'clamp(16px, 2vw, 24px)', fontWeight: 900, color: '#fff', marginBottom: 8 }}>{t('datenschutz.ctaTitle')}</h2>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', marginBottom: 20 }}>{t('datenschutz.ctaDesc')}</p>
          <Link to="/kontakt" style={{ display: 'inline-flex', alignItems: 'center', padding: '11px 26px', borderRadius: 11, background: '#fff', color: accent, fontWeight: 700, fontSize: 13, textDecoration: 'none', boxShadow: '0 6px 20px rgba(0,0,0,0.2)' }}>
            {t('datenschutz.ctaButton')}
          </Link>
        </div>
      </section>
    </div>
  );
}
